import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  useToast,
  VStack,
  Alert,
  AlertIcon,
  Heading,
  Text,
  Card,
  CardBody,
  Radio,
  RadioGroup,
  Stack,
  Badge,
  IconButton,
  Spinner,
} from '@chakra-ui/react';
import { db } from '../../config/firebase';
import { doc, getDoc, updateDoc, setDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { FiVolume2, FiVolumeX } from 'react-icons/fi';

// Cast to React.ElementType to fix TypeScript errors
const FiVolume2Icon = FiVolume2 as React.ElementType;
const FiVolumeXIcon = FiVolumeX as React.ElementType;

// Voice options from Google Cloud TTS Chirp 3: HD voices
export interface VoiceOption {
  name: string;
  gender: 'Male' | 'Female';
}

const VOICE_OPTIONS: VoiceOption[] = [
  { name: 'Achernar', gender: 'Female' },
  { name: 'Achird', gender: 'Male' },
  { name: 'Algenib', gender: 'Male' },
  { name: 'Algieba', gender: 'Male' },
  { name: 'Alnilam', gender: 'Male' },
  { name: 'Aoede', gender: 'Female' },
  { name: 'Autonoe', gender: 'Female' },
  { name: 'Callirrhoe', gender: 'Female' },
  { name: 'Charon', gender: 'Male' },
  { name: 'Despina', gender: 'Female' },
  { name: 'Enceladus', gender: 'Male' },
  { name: 'Erinome', gender: 'Female' },
  { name: 'Fenrir', gender: 'Male' },
  { name: 'Gacrux', gender: 'Female' },
  { name: 'Iapetus', gender: 'Male' },
  { name: 'Kore', gender: 'Female' },
  { name: 'Laomedeia', gender: 'Female' },
  { name: 'Leda', gender: 'Female' },
  { name: 'Orus', gender: 'Male' },
  { name: 'Pulcherrima', gender: 'Female' },
  { name: 'Puck', gender: 'Male' },
  { name: 'Rasalgethi', gender: 'Male' },
  { name: 'Sadachbia', gender: 'Male' },
  { name: 'Sadaltager', gender: 'Male' },
  { name: 'Schedar', gender: 'Male' },
  { name: 'Sulafat', gender: 'Female' },
  { name: 'Umbriel', gender: 'Male' },
  { name: 'Vindemiatrix', gender: 'Female' },
  { name: 'Zephyr', gender: 'Female' },
  { name: 'Zubenelgenubi', gender: 'Male' },
];

const SAMPLE_TEXT = "Hello! I'm Nibi, your AI assistant. How can I help you today?";

export interface VoiceSettings {
  voiceName: string;
  updatedAt?: Timestamp;
  updatedBy?: string;
}

const DEFAULT_VOICE_SETTINGS: VoiceSettings = {
  voiceName: 'Zephyr',
};

const VoiceSettingsComponent: React.FC = () => {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<VoiceSettings>(DEFAULT_VOICE_SETTINGS);
  const [playingVoice, setPlayingVoice] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      // Try to get from appSettings document
      const settingsRef = doc(db, 'appSettings', 'default');
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        const data = settingsSnap.data();
        if (data.nibiVoiceSettings) {
          setSettings(data.nibiVoiceSettings as VoiceSettings);
        } else if (data.voiceName) {
          // Legacy support
          setSettings({ voiceName: data.voiceName });
        }
      }
    } catch (error) {
      console.error('Error loading voice settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load voice settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const settingsRef = doc(db, 'appSettings', 'default');
      const settingsSnap = await getDoc(settingsRef);

      const updateData = {
        nibiVoiceSettings: {
          voiceName: settings.voiceName,
          updatedAt: Timestamp.now(),
          updatedBy: currentUser?.email || 'unknown',
        },
        updatedAt: Timestamp.now(),
      };

      if (settingsSnap.exists()) {
        await updateDoc(settingsRef, updateData);
      } else {
        await setDoc(settingsRef, {
          ...updateData,
          createdAt: Timestamp.now(),
        });
      }

      toast({
        title: 'Success',
        description: 'Voice settings saved successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error saving voice settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save voice settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const playVoiceSample = async (voiceName: string) => {
    try {
      // Stop any currently playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      setPlayingVoice(voiceName);

      // Use Google Cloud Text-to-Speech API to generate sample
      // Note: This requires a backend endpoint or direct API call
      // For now, we'll use a simple approach with the browser's SpeechSynthesis API as a fallback
      // In production, you'd want to call your backend which uses Google Cloud TTS
      
      // Using Web Speech API as a temporary solution
      // In production, replace this with a call to your backend TTS service
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(SAMPLE_TEXT);
        utterance.rate = 1.0;
        utterance.pitch = 1.0;
        utterance.volume = 1.0;
        
        // Try to find a voice that matches (browser voices won't match exactly)
        const voices = speechSynthesis.getVoices();
        const matchingVoice = voices.find(v => 
          v.name.toLowerCase().includes(voiceName.toLowerCase()) ||
          (voiceName === 'Zephyr' && v.name.toLowerCase().includes('female'))
        );
        
        if (matchingVoice) {
          utterance.voice = matchingVoice;
        }
        
        utterance.onend = () => {
          setPlayingVoice(null);
        };
        
        utterance.onerror = () => {
          setPlayingVoice(null);
          toast({
            title: 'Playback Error',
            description: 'Could not play voice sample. Please test the voice in the app.',
            status: 'warning',
            duration: 3000,
            isClosable: true,
          });
        };
        
        speechSynthesis.speak(utterance);
      } else {
        toast({
          title: 'Not Supported',
          description: 'Voice playback is not supported in this browser. Please test the voice in the app.',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
        setPlayingVoice(null);
      }
    } catch (error) {
      console.error('Error playing voice sample:', error);
      setPlayingVoice(null);
      toast({
        title: 'Error',
        description: 'Failed to play voice sample',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const stopPlayback = () => {
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingVoice(null);
  };

  if (loading) {
    return (
      <Box>
        <HStack mb={6} align="center">
          <Heading size="lg">Voice Settings</Heading>
        </HStack>
        <Spinner size="lg" />
      </Box>
    );
  }

  return (
    <Box>
      <HStack mb={6} align="center">
        <Heading size="lg">Voice Settings</Heading>
      </HStack>

      <Alert status="info" mb={6}>
        <AlertIcon />
        <Box>
          <Text fontSize="sm" fontWeight="medium" mb={1}>
            Nibi AI Assistant Voice
          </Text>
          <Text fontSize="sm">
            Select the voice for the Nibi AI assistant. Changes will apply to new voice conversations.
            The selected voice will be used for all voice-enabled interactions.
          </Text>
        </Box>
      </Alert>

      <Card mb={6}>
        <CardBody>
          <VStack align="stretch" spacing={6}>
            <FormControl>
              <FormLabel>Select Voice</FormLabel>
              <RadioGroup
                value={settings.voiceName}
                onChange={(value) => setSettings({ ...settings, voiceName: value })}
              >
                <Stack spacing={4}>
                  {VOICE_OPTIONS.map((voice) => (
                    <Box
                      key={voice.name}
                      p={4}
                      borderWidth="1px"
                      borderRadius="md"
                      _hover={{ bg: 'gray.50' }}
                      borderColor={settings.voiceName === voice.name ? 'blue.500' : 'gray.200'}
                    >
                      <HStack justify="space-between">
                        <HStack spacing={4}>
                          <Radio value={voice.name} />
                          <VStack align="start" spacing={1}>
                            <HStack>
                              <Text fontWeight="medium">{voice.name}</Text>
                              <Badge
                                colorScheme={voice.gender === 'Female' ? 'pink' : 'blue'}
                                fontSize="xs"
                              >
                                {voice.gender}
                              </Badge>
                            </HStack>
                          </VStack>
                        </HStack>
                        <IconButton
                          aria-label={`Play sample for ${voice.name}`}
                          icon={playingVoice === voice.name ? <FiVolumeXIcon /> : <FiVolume2Icon />}
                          size="sm"
                          onClick={() => {
                            if (playingVoice === voice.name) {
                              stopPlayback();
                            } else {
                              playVoiceSample(voice.name);
                            }
                          }}
                          isLoading={playingVoice === voice.name}
                          variant="ghost"
                        />
                      </HStack>
                    </Box>
                  ))}
                </Stack>
              </RadioGroup>
            </FormControl>

            <HStack justify="flex-end" pt={4}>
              <Button
                colorScheme="blue"
                onClick={handleSave}
                isLoading={saving}
                loadingText="Saving..."
              >
                Save Settings
              </Button>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      <Alert status="warning" mb={6}>
        <AlertIcon />
        <Box>
          <Text fontSize="sm" fontWeight="medium" mb={1}>
            Note on Voice Samples
          </Text>
          <Text fontSize="sm">
            The browser voice samples are approximate. For the most accurate preview, test the selected voice
            directly in the web app's voice mode. The actual voice quality will match Google Cloud's Chirp 3: HD voices.
          </Text>
        </Box>
      </Alert>
    </Box>
  );
};

export default VoiceSettingsComponent;

